/**
 * 마크다운 전처리 함수들
 * 마크다운 파싱 전에 텍스트를 정규화하여 올바른 파싱을 보장합니다.
 */

// ============================================================================
// 정규식 상수
// ============================================================================

const ASTERISK_VARIANTS_REGEX = /[＊∗⁎﹡]/g;
const FULLWIDTH_UNDERSCORE_REGEX = /＿/g;
const INVISIBLE_BETWEEN_DELIMITERS_REGEX = /([*_])[\u200B\u200C\u2060\uFEFF]+(?=[*_])/g;

export const FENCE_REGEX = /^(?:\s*)(`{3,}|~{3,})(?:\s*).*$/; // 3개 이상의 ` 또는 ~로 시작하는 라인
const TABLE_HEADER_REGEX = /^(\s{4,})\|.+\|?\s*$/; // 4개 이상의 공백 뒤에 |로 시작하는 라인
const TABLE_DELIMITER_REGEX = /^(\s{4,})\|?(?:\s*:?-+:?\s*\|)+(?:\s*:?-+:?\s*)\|?\s*$/;
const QUICK_TABLE_CHECK = /^\s{4,}\|/m; // 4개 이상의 공백 뒤에 |로 시작하는 라인

// 날짜 이스케이프 정규식 (성능 최적화를 위해 모듈 레벨에서 컴파일)
// 1) 한 줄짜리 날짜(예: 2025. 8. 21.) 매칭
const SINGLE_LINE_DATE_REGEX = /(^|\n)([ \t]*)(\d{4})\.([ \t]*)(\d{1,2})\.([ \t]*)(\d{1,2})(\.)?/g;
// 2) 리스트 아이템 시작(예: "1. 2025. 3. 12.자")에서 날짜 매칭
const LIST_ITEM_DATE_REGEX = /(^|\n)([ \t]*)(\d+[.)]|[-*+])([ \t]+)(\d{4})\.([ \t]*)(\d{1,2})\.([ \t]*)(\d{1,2})(\.)?/g;

// 리스트 아이템의 전체 접두사(들여쓰기 + 마커) 추출
export const LIST_ITEM_PREFIX_REGEX = /^([ \t]*)(?:[-*+]|(?:\d+[.)]))[ \t]+/;

export const normalizeBacktickedLinks = (markdown: string): string => {
    // 비문자열 타입만 경고, 빈 문자열은 정상 입력
    if (typeof markdown !== 'string') {
        console.warn('[normalizeBacktickedLinks] 문자열이 아닌 값 전달됨:', { type: typeof markdown, value: markdown });
        return '';
    }
    if (!markdown) return '';

    return markdown.replace(/`(\[[^\]]+\]\([^\)]+\))`/g, '$1');
};

/**
 * 깨진 마크다운 링크를 복원합니다.
 * LLM이 생성한 마크다운에서 URL 내 괄호/대괄호로 인해 링크 문법이 깨진 경우를 수정합니다.
 *
 * 패턴: (URL_broken) text](URL_correct) → [text](<URL_correct>)
 * 예: (https://law.go.kr/판례/(2016두5942[3)) 판결](https://law.go.kr/판례/(2016두59430))
 *   → [판결](<https://law.go.kr/판례/(2016두59430)>)
 */
export const fixBrokenMarkdownLinks = (markdown: string): string => {
    if (typeof markdown !== 'string') {
        console.warn('[fixBrokenMarkdownLinks] 문자열이 아닌 값 전달됨:', { type: typeof markdown, value: markdown });
        return '';
    }
    if (!markdown) return '';
    // 빠른 체크: ](http 패턴이 없으면 깨진 링크가 없음
    if (!markdown.includes('](http')) return markdown;

    return markdown.replace(
        /\(https?:\/\/[^\s]*?\)\)+\s+([^\]\[\n]+)\]\((https?:\/\/(?:[^\s()]|\([^)]*\))*)\)/g,
        (_match, text: string, url: string) => {
            return `[${text.trim()}](<${url}>)`;
        },
    );
};

/**
 * URL에 괄호나 대괄호가 포함된 마크다운 링크를 정규화합니다.
 * marked.js는 URL 내부의 괄호를 링크 종료로 오인할 수 있으므로,
 * 이러한 URL을 꺾쇠괄호 <>로 감싸서 올바르게 파싱되도록 합니다.
 *
 * 예: [텍스트](https://example.com/path(2016)) → [텍스트](<https://example.com/path(2016)>)
 *
 * @param markdown - 원본 마크다운 문자열
 * @returns 정규화된 마크다운 문자열
 */
/**
 * URL 내 소괄호/대괄호의 균형을 검증합니다.
 * 불균형한 경우, markdown 링크 종료 문자를 URL 일부로 오인한 가능성이 높습니다.
 */
const hasBalancedBrackets = (url: string): boolean => {
    const stack: string[] = [];

    for (const char of url) {
        if (char === '(' || char === '[') {
            stack.push(char);
            continue;
        }

        if (char === ')') {
            if (stack.pop() !== '(') return false;
            continue;
        }

        if (char === ']') {
            if (stack.pop() !== '[') return false;
        }
    }

    return stack.length === 0;
};

export const normalizeLinksWithParentheses = (markdown: string): string => {
    if (typeof markdown !== 'string') {
        console.warn('[normalizeLinksWithParentheses] 문자열이 아닌 값 전달됨:', { type: typeof markdown, value: markdown });
        return '';
    }
    if (!markdown) return '';

    // 마크다운 링크 패턴: [텍스트](URL)
    // URL에 괄호 () 또는 대괄호 []가 있으면 <>로 감싸기
    // 이미 <>로 감싸져 있으면 건너뛰기
    // NOTE: URL에는 공백이 없으므로 \s를 제외하여, 링크 뒤의 괄호 부가설명까지 과잉 매칭하는 것을 방지
    return markdown.replace(/\[([^\]]+)\]\(([^<>)\s]+[()[\]][^)\s]*)\)/g, (match, text, url: string) => {
        // URL이 이미 <>로 감싸져 있는지 확인
        if (url.startsWith('<') && url.endsWith('>')) {
            return match; // 이미 감싸져 있으면 그대로 반환
        }

        // URL 내 괄호가 불균형하면, 정규식이 markdown 닫는 )를 URL 일부로 오인한 것
        // 이 경우 원본 그대로 반환하여 marked.js가 정상 파싱하도록 함
        if (!hasBalancedBrackets(url)) {
            return match;
        }

        // URL에 괄호나 대괄호가 있으면 <>로 감싸기
        if (/[()[\]]/.test(url)) {
            return `[${text}](<${url}>)`;
        }

        return match;
    });
};

// ============================================================================
// 인라인 코드 보호/복원
// ============================================================================

export interface InlineCodeProtector {
    protect: (markdown: string) => string;
    restore: (html: string) => string;
}

/**
 * 인라인 코드 보호/복원을 위한 클로저 생성
 * 각 파싱 호출마다 독립적인 토큰 맵을 사용하여 동시성 문제 방지
 */
export const createInlineCodeProtector = (): InlineCodeProtector => {
    const codeTokenMap = new Map<string, string>();
    // 보호기별 고유 토큰 ID로 사용자 입력과의 충돌을 회피
    const protectorId = Math.random().toString(36).slice(2);

    /**
     * 인라인 코드(`)를 임시 토큰으로 변환하여 다른 마크다운 문법(strong 등)과의 충돌을 방지
     * 예: **`code`** -> **@@CODE_TOKEN_<id>_0@@** -> <strong><code>code</code></strong>
     * 주의: fenced code block(``` / ~~~) 내부는 변환하지 않음
     */
    const protect = (markdown: string): string => {
        if (!markdown) return markdown;

        const lines = markdown.split(/\r?\n/);
        const out: string[] = [];

        let inFence = false;
        let fenceChar = '';
        let fenceLen = 0;

        // 백틱(`)으로 감싸진 인라인 코드 패턴 (최대 3개의 백틱까지 처리)
        // 탐욕적이지 않게 매칭하여 여러 코드 블록을 개별적으로 처리
        const INLINE_CODE_REGEX = /(`{1,3})((?:[^`]|(?!\1)`)*)\1/g;

        for (const line of lines) {
            // fenced code block(``` / ~~~) 처리
            const fenceMatch = FENCE_REGEX.exec(line);
            if (fenceMatch) {
                const fence = fenceMatch[1];
                const char = fence[0];

                if (!inFence) {
                    inFence = true;
                    fenceChar = char;
                    fenceLen = fence.length;
                } else if (char === fenceChar && fence.length >= fenceLen) {
                    inFence = false;
                    fenceChar = '';
                    fenceLen = 0;
                }

                out.push(line);
                continue;
            }

            // fenced code block 내부는 변환하지 않음
            if (inFence) {
                out.push(line);
                continue;
            }

            // 인라인 코드 토큰화
            const processedLine = line.replace(INLINE_CODE_REGEX, (match, delimiter, codeContent) => {
                const content = codeContent || '';

                // REF 태그인 경우 토큰화하지 않고 원본 유지 (refPlugin이 처리하도록)
                // 예: `[#REF:doc:id=123]` -> [#REF:doc:id=123] (백틱 제거, REF로 처리됨)
                if (/^\[#REF:[^\]]+\]$/.test(content)) {
                    return content; // 백틱 없이 REF 태그만 반환
                }

                // 마크다운 강조 문법과 충돌하지 않도록 @@...@@ 형태의 토큰 사용
                const token = `@@CODE_TOKEN_${protectorId}_${codeTokenMap.size}@@`;
                // HTML 이스케이프 처리
                const escapedContent = content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

                const html = `<code class="bg-gray-200 text-red-600 px-1 rounded">${escapedContent}</code>`;
                codeTokenMap.set(token, html);
                return token;
            });

            out.push(processedLine);
        }

        return out.join('\n');
    };

    /**
     * 임시 토큰을 원래 인라인 코드 HTML로 복원
     */
    const restore = (html: string): string => {
        if (!html || codeTokenMap.size === 0) return html;

        // 토큰을 원래 코드로 복원
        let result = html;
        codeTokenMap.forEach((replacement, token) => {
            result = result.split(token).join(replacement);
        });
        return result;
    };

    return { protect, restore };
};

// 레거시 호환성을 위한 기본 인스턴스 (단일 스레드 환경에서만 사용)
// 주의: 동시 파싱 시에는 createInlineCodeProtector()를 사용해야 함
const defaultProtector = createInlineCodeProtector();
export const protectInlineCode = defaultProtector.protect;
export const restoreInlineCode = defaultProtector.restore;

// ============================================================================
// 강조 구분자 정규화
// ============================================================================

/**
 * fullwidth asterisk(＊) / zero-width 문자가 섞여 **strong** 문법이 깨지는 케이스를 정규화
 */
export const normalizeEmphasisDelimiters = (markdown: string): string => {
    if (!markdown) return markdown;

    return markdown.replace(ASTERISK_VARIANTS_REGEX, '*').replace(FULLWIDTH_UNDERSCORE_REGEX, '_').replace(INVISIBLE_BETWEEN_DELIMITERS_REGEX, '$1');
};

// ============================================================================
// 날짜 이스케이프
// ============================================================================

/**
 * 날짜 유효성 검사 헬퍼 함수
 * @param year 연도 (1900-2100)
 * @param month 월 (1-12)
 * @param day 일 (1-31)
 * @returns 유효한 날짜 범위이면 true
 */
const isValidDateRange = (year: number, month: number, day: number): boolean => {
    if (!Number.isInteger(year) || year < 1900 || year > 2100) return false;
    if (!Number.isInteger(month) || month < 1 || month > 12) return false;
    if (!Number.isInteger(day) || day < 1 || day > 31) return false;
    return true;
};

/**
 * 날짜(예: 2025. 8. 21.)가 마크다운 ordered list로 파싱되는 것을 방지
 */
export const escapeLeadingDateForMarkdown = (markdown: string): string => {
    if (!markdown) return markdown;

    // 정규식의 lastIndex 초기화 (global flag 사용 시 필수)
    SINGLE_LINE_DATE_REGEX.lastIndex = 0;
    LIST_ITEM_DATE_REGEX.lastIndex = 0;

    // 1) 한 줄짜리 날짜(예: 2025. 8. 21.) 보호
    const singleLineEscaped = markdown.replace(
        SINGLE_LINE_DATE_REGEX,
        (match, lineBreak: string, indent: string, yearRaw: string, spaceAfterYear: string, monthRaw: string, spaceAfterMonth: string, dayRaw: string, trailingDot?: string) => {
            if (!isValidDateRange(Number(yearRaw), Number(monthRaw), Number(dayRaw))) {
                return match;
            }
            return `${lineBreak}${indent}${yearRaw}\\.${spaceAfterYear}${monthRaw}\\.${spaceAfterMonth}${dayRaw}${trailingDot ? '\\.' : ''}`;
        },
    );

    // 2) 리스트 아이템 시작(예: "1. 2025. 3. 12.자")에서 날짜가 중첩 ordered list로 파싱되는 것을 방지
    return singleLineEscaped.replace(
        LIST_ITEM_DATE_REGEX,
        (
            match,
            lineBreak: string,
            indent: string,
            listMarker: string,
            afterMarker: string,
            yearRaw: string,
            spaceAfterYear: string,
            monthRaw: string,
            spaceAfterMonth: string,
            dayRaw: string,
            trailingDot?: string,
        ) => {
            if (!isValidDateRange(Number(yearRaw), Number(monthRaw), Number(dayRaw))) {
                return match;
            }
            return `${lineBreak}${indent}${listMarker}${afterMarker}${yearRaw}\\.${spaceAfterYear}${monthRaw}\\.${spaceAfterMonth}${dayRaw}${trailingDot ? '\\.' : ''}`;
        },
    );
};

// ============================================================================
// 들여쓰기 리스트 정규화
// ============================================================================

/**
 * Markdown에서는 줄 시작 공백 4칸(또는 탭 1개)이 indented code block 규칙으로 해석됩니다.
 * fenced code block 밖에서만 "4칸 이상 들여쓰기된 리스트 마커"를 3칸 들여쓰기로 보정하여
 * 리스트로 파싱되게 합니다.
 */
export const normalizeIndentedLists = (markdown: string): string => {
    // 4칸 이상 들여쓰기된 리스트 아이템이 있는지 확인 (전체 검사)
    const INDENTED_LIST_CHECK_REGEX = /^(?:[ ]{4,}|[ ]{0,3}\t)(?:[-*+]\s+|\d+[.)]\s+)/m;
    // 4칸 이상 들여쓰기된 부분만 매칭 (Lookahead로 리스트 마커 확인)
    const INDENTED_LIST_LINE_REGEX = /^(?:[ ]{4,}|[ ]{0,3}\t)(?=(?:[-*+]\s+|\d+[.)]\s+))/;
    // 두 자리 수 이상의 순서 있는 리스트(10. 등)가 있는지 확인
    const MULTI_DIGIT_ORDERED_LIST_ITEM_CHECK_REGEX = /^[ \t]*\d{2,}[.)][ \t]+/m;
    // 두 자리 수 이상의 순서 있는 리스트 라인 매칭
    const MULTI_DIGIT_ORDERED_LIST_ITEM_LINE_REGEX = /^([ \t]*\d{2,}[.)][ \t]+)/;
    // 순서 없는 리스트(-, *, +) 라인 매칭
    const UNORDERED_LIST_MARKER_LINE_REGEX = /^([ \t]+)([-*+])[ \t]+/;

    const getVisualWidth = (text: string): number => {
        let width = 0;
        for (const char of text) {
            width += char === '\t' ? 4 : 1;
        }
        return width;
    };

    const getLeadingIndentWidth = (line: string): number => {
        const indentMatch = /^([ \t]*)/.exec(line);
        if (!indentMatch) return 0;
        return getVisualWidth(indentMatch[1]);
    };

    const getListItemContentIndentWidth = (line: string): number | null => {
        const match = LIST_ITEM_PREFIX_REGEX.exec(line);
        if (!match) return null;
        return getVisualWidth(match[0]);
    };

    const isLikelyNestedListMarker = (lines: string[], lineIndex: number): boolean => {
        const currentIndent = getLeadingIndentWidth(lines[lineIndex] || '');

        for (let i = lineIndex - 1; i >= 0; i -= 1) {
            const prevLine = lines[i] || '';
            if (!prevLine.trim()) continue;
            if (FENCE_REGEX.test(prevLine)) return false;

            const prevIndent = getLeadingIndentWidth(prevLine);
            if (prevIndent >= currentIndent) continue;

            const parentContentIndent = getListItemContentIndentWidth(prevLine);
            if (!parentContentIndent) return false;

            return currentIndent >= parentContentIndent;
        }

        return false;
    };

    // 4칸 이상 들여쓰기된 일반 텍스트 체크 (리스트가 아닌 경우, code block 오인식 방지용)
    const INDENTED_TEXT_CHECK_REGEX = /^(?:[ ]{4,}|[ ]{0,3}\t)(?![-*+]\s|\d+[.)]\s)/m;
    // 중첩 리스트 후보 체크 (1칸 이상 들여쓰기된 리스트 아이템)
    // 예: "  1. " (2칸 들여쓰기)
    const NESTED_LIST_CANDIDATE_REGEX = /^[ \t]+(?:[-*+]|(?:\d+[.)]))[ \t]+/m;

    const hasIndentedListOrText =
        INDENTED_LIST_CHECK_REGEX.test(markdown) || MULTI_DIGIT_ORDERED_LIST_ITEM_CHECK_REGEX.test(markdown) || INDENTED_TEXT_CHECK_REGEX.test(markdown) || NESTED_LIST_CANDIDATE_REGEX.test(markdown);
    if (!markdown || !hasIndentedListOrText) {
        return markdown;
    }

    const lines = markdown.split(/\r?\n/);
    const out: string[] = [];

    let inFence = false;
    let fenceChar = '';
    let fenceLen = 0;

    for (let i = 0; i < lines.length; i += 1) {
        let line = lines[i];
        // fenced code block(``` / ~~~) 내부는 변경하지 않음
        const fenceMatch = FENCE_REGEX.exec(line);
        if (fenceMatch) {
            const fence = fenceMatch[1];
            const char = fence[0];

            if (!inFence) {
                inFence = true;
                fenceChar = char;
                fenceLen = fence.length;
            } else if (char === fenceChar && fence.length >= fenceLen) {
                inFence = false;
                fenceChar = '';
                fenceLen = 0;
            }

            out.push(line);
            continue;
        }

        if (!inFence) {
            // "약간 들여쓰기된" 리스트 아이템이 부모 리스트 아이템의 내용 시작 위치보다 덜 들여쓰기되어
            // 중첩이 깨지는 경우(sibling으로 파싱됨)를 감지하여 보정
            // 예:
            // 1. 부모 (content start: 3칸)
            //   1) 자식 (indent: 2칸) -> 3칸 미만이므로 CommonMark에서 중첩 안됨 -> 3칸으로 보정 필요

            const listItemMatch = LIST_ITEM_PREFIX_REGEX.exec(line);
            if (listItemMatch) {
                const currentIndentWidth = getVisualWidth(listItemMatch[1]);

                if (currentIndentWidth > 0) {
                    for (let j = i - 1; j >= 0; j -= 1) {
                        const prevLine = lines[j] || '';
                        if (!prevLine.trim()) continue;
                        if (FENCE_REGEX.test(prevLine)) break; // 코드 블록 만나면 중단

                        // 이전 라인도 리스트 아이템이어야 함
                        const parentMatch = LIST_ITEM_PREFIX_REGEX.exec(prevLine);
                        if (!parentMatch) break; // 바로 위(혹은 공백 건너뜀)가 리스트가 아니면 중첩 아님

                        const parentIndentWidth = getVisualWidth(parentMatch[1]);

                        // 부모보다 더 들여쓰기 되어 있어야 중첩 의도로 봄
                        // 같거나 큰 경우는 형제(sibling)이므로 건너뛰고 실제 부모를 찾는다
                        // (이전 형제가 이미 패딩 보정된 경우에도 올바르게 부모를 탐색)
                        if (currentIndentWidth <= parentIndentWidth) continue;

                        // 부모의 콘텐츠 시작 위치 계산
                        // parentMatch[0]는 "  1. " 까지의 문자열 (공백+마커+공백)
                        const parentContentIndentWidth = getVisualWidth(parentMatch[0]);

                        // 현재 들여쓰기가 부모 콘텐츠 시작 위치보다 작으면 "중첩 깨짐" 발생
                        if (currentIndentWidth < parentContentIndentWidth) {
                            // 부족한 만큼 공백 추가
                            const padding = ' '.repeat(parentContentIndentWidth - currentIndentWidth);
                            line = `${padding}${line}`;
                            lines[i] = line; // 현재 줄 업데이트
                        }

                        break; // 가장 가까운 부모 리스트만 확인하고 종료
                    }
                }
            }

            if (INDENTED_LIST_LINE_REGEX.test(line)) {
                // 중첩 리스트의 들여쓰기는 유지해야 함
                if (!isLikelyNestedListMarker(lines, i)) {
                    line = line.replace(INDENTED_LIST_LINE_REGEX, '   ');
                    lines[i] = line;
                }
            } else {
                // 리스트가 아닌 4칸 이상 들여쓰기된 일반 텍스트도 3칸으로 정규화
                // (마크다운에서 4칸 이상 들여쓰기는 code block으로 해석됨)
                const generalIndentMatch = /^([ ]{4,}|[ ]{0,3}\t)(.+)$/.exec(line);
                if (generalIndentMatch && line.trim()) {
                    // 테이블 라인(|로 시작)은 제외 (normalizeMarkdownTables에서 처리)
                    if (!line.trim().startsWith('|')) {
                        line = '   ' + generalIndentMatch[2];
                        lines[i] = line;
                    }
                }
            }
        }

        out.push(line);
    }

    return out.join('\n');
};

// ============================================================================
// 테이블 정규화
// ============================================================================

/**
 * marked(GFM)은 리스트 아이템 안에서 "깊게" 들여쓰기된 테이블(파이프 테이블)을 테이블로 인식하지 못하고
 * 단순 텍스트로 렌더링하는 경우가 있어, 테이블 블록만 최소한으로 outdent 해줍니다.
 */
export const normalizeMarkdownTables = (markdown: string): string => {
    // Early return: 빈 문자열이거나 들여쓰기된 테이블 패턴이 없으면 즉시 반환
    if (!markdown || !QUICK_TABLE_CHECK.test(markdown)) {
        return markdown;
    }

    const lines = markdown.split(/\r?\n/);
    const out: string[] = [];

    let inFence = false;
    let fenceChar = '';
    let fenceLen = 0;

    for (let i = 0; i < lines.length; ) {
        const line = lines[i];

        // fenced code block(``` / ~~~) 처리
        const fenceMatch = FENCE_REGEX.exec(line);
        if (fenceMatch) {
            const fence = fenceMatch[1];
            const char = fence[0];

            if (!inFence) {
                inFence = true;
                fenceChar = char;
                fenceLen = fence.length;
            } else if (char === fenceChar && fence.length >= fenceLen) {
                inFence = false;
                fenceChar = '';
                fenceLen = 0;
            }

            out.push(line);
            i += 1;
            continue;
        }

        if (!inFence) {
            const next = lines[i + 1];

            // 테이블 헤더 라인 + 구분자 라인 패턴 확인
            const headerMatch = TABLE_HEADER_REGEX.exec(line);
            const delimiterMatch = next ? TABLE_DELIMITER_REGEX.exec(next) : null;

            if (headerMatch && delimiterMatch) {
                const indent = Math.min(headerMatch[1].length, delimiterMatch[1].length);
                const indentStr = headerMatch[1].substring(0, indent);

                // 연속된 테이블 라인만 outdent
                while (i < lines.length) {
                    const currentLine = lines[i];
                    if (!currentLine.startsWith(indentStr)) break;

                    const trimmed = currentLine.substring(indent);
                    if (trimmed[0] !== '|') break;

                    out.push(trimmed);
                    i += 1;
                }
                continue;
            }
        }

        out.push(line);
        i += 1;
    }

    return out.join('\n');
};
